/**
 * Game-wide constants
 */
export const GAME_STATES = {
  LOADING: 'loading',
  INTRO: 'intro',               // Name entry screen
  ON_PLANET: 'on_planet',
  IN_SHIP: 'in_ship',           // Walking around inside the ship
  IN_COCKPIT: 'in_cockpit',     // Seated in cockpit, can fly
  FLYING: 'flying',             // Actively piloting
  GALAXY_MAP: 'galaxy_map',
  WARPING: 'warping',
  JOURNAL: 'journal',           // Species journal overlay
  ENDGAME: 'endgame',           // Drone-shot reveal sequence
  MUSIC_MAKER: 'music_maker',   // GarageBand-style composition screen
};

export const PLANET_TYPES = ['rocky', 'gas', 'ice', 'lava', 'ocean', 'crystal', 'forest', 'desert'];

/**
 * Planet musical genres — determines the overall sonic character of each planet.
 * Each genre biases instrument selection, pattern generation, tempo, and feel.
 */
export const PLANET_GENRES = {
  orchestral:  { label: 'Orchestral',  tempoRange: [60, 100],  swingAmount: 0,    reverbMix: 0.35, filterCutoff: 3000, droneLevel: 0.5  },
  ambient:     { label: 'Ambient',     tempoRange: [40, 70],   swingAmount: 0,    reverbMix: 0.7,  filterCutoff: 1800, droneLevel: 0.8  },
  jazz:        { label: 'Jazz',        tempoRange: [80, 140],  swingAmount: 0.55, reverbMix: 0.3,  filterCutoff: 4000, droneLevel: 0.15 },
  breakcore:   { label: 'Breakcore',   tempoRange: [150, 220], swingAmount: 0,    reverbMix: 0.15, filterCutoff: 6000, droneLevel: 0.05 },
  electronic:  { label: 'Electronic',  tempoRange: [110, 145], swingAmount: 0,    reverbMix: 0.25, filterCutoff: 5000, droneLevel: 0.3  },
  minimal:     { label: 'Minimal',     tempoRange: [90, 130],  swingAmount: 0,    reverbMix: 0.45, filterCutoff: 2500, droneLevel: 0.4  },
  folkAcoustic:{ label: 'Folk',        tempoRange: [70, 120],  swingAmount: 0.1,  reverbMix: 0.2,  filterCutoff: 3500, droneLevel: 0.25 },
  cinematic:   { label: 'Cinematic',   tempoRange: [50, 90],   swingAmount: 0,    reverbMix: 0.6,  filterCutoff: 2000, droneLevel: 0.7  },
  lofi:        { label: 'Lo-Fi',       tempoRange: [65, 90],   swingAmount: 0.35, reverbMix: 0.4,  filterCutoff: 2200, droneLevel: 0.35 },
  world:       { label: 'World',       tempoRange: [80, 130],  swingAmount: 0.2,  reverbMix: 0.3,  filterCutoff: 3500, droneLevel: 0.3  },
};

export const PLANET_GENRE_NAMES = Object.keys(PLANET_GENRES);

/**
 * System-level musical styles — each solar system has a "vibe" that
 * influences all its planets. Defines time signature, BPM range, and
 * preferred genre weights.
 */
export const SYSTEM_MUSICAL_STYLES = {
  standard:   { label: 'Standard',   timeSig: [4, 4], genreWeights: { orchestral: 3, ambient: 2, jazz: 1, electronic: 2, folkAcoustic: 2, lofi: 1, minimal: 1, cinematic: 1, world: 1 } },
  waltz:      { label: 'Waltz',      timeSig: [3, 4], genreWeights: { orchestral: 3, jazz: 2, folkAcoustic: 3, ambient: 1, cinematic: 2 } },
  complex:    { label: 'Complex',    timeSig: [5, 4], genreWeights: { jazz: 3, world: 2, minimal: 1, electronic: 1 } },
  compound:   { label: 'Compound',   timeSig: [6, 8], genreWeights: { folkAcoustic: 3, world: 3, jazz: 2, orchestral: 2, lofi: 1 } },
  asymmetric: { label: 'Asymmetric', timeSig: [7, 8], genreWeights: { jazz: 3, world: 3, electronic: 1, breakcore: 1 } },
  driving:    { label: 'Driving',    timeSig: [4, 4], genreWeights: { breakcore: 3, electronic: 3, minimal: 1, jazz: 1 } },
  freeTime:   { label: 'Free Time',  timeSig: [4, 4], genreWeights: { ambient: 4, cinematic: 3, lofi: 1, minimal: 1 } },
};

export const SYSTEM_STYLE_NAMES = Object.keys(SYSTEM_MUSICAL_STYLES);

export const STAR_CLASSES = [
  { type: 'O', color: 0x9bb0ff, temp: 'hot',    size: [2, 4] },
  { type: 'B', color: 0xaabfff, temp: 'hot',    size: [1.5, 3] },
  { type: 'A', color: 0xcad7ff, temp: 'warm',   size: [1.2, 2] },
  { type: 'F', color: 0xf8f7ff, temp: 'warm',   size: [1, 1.5] },
  { type: 'G', color: 0xfff4ea, temp: 'medium', size: [0.8, 1.2] },
  { type: 'K', color: 0xffd2a1, temp: 'cool',   size: [0.6, 1] },
  { type: 'M', color: 0xffcc6f, temp: 'cold',   size: [0.3, 0.7] },
];

export const CREATURE_BODY_CONFIG = {
  blob:      { model: null, scale: 1.0, yOffset: 0 },
  quadruped: { model: null, scale: 1.0, yOffset: 0.2 },
  biped:     { model: null, scale: 1.0, yOffset: 0.3 },
  serpent:   { model: null, scale: 1.0, yOffset: 0.1 },
  floater:   { model: null, scale: 1.0, yOffset: 1.0 },
  insectoid: { model: null, scale: 1.0, yOffset: 0.2 },
};

export const CREATURE_BODY_TYPES = Object.keys(CREATURE_BODY_CONFIG);


export const MUSIC_SCALES = {
  major:      [0, 2, 4, 5, 7, 9, 11],
  minor:      [0, 2, 3, 5, 7, 8, 10],
  pentatonic: [0, 2, 4, 7, 9],
  blues:      [0, 3, 5, 6, 7, 10],
  dorian:     [0, 2, 3, 5, 7, 9, 10],
  phrygian:   [0, 1, 3, 5, 7, 8, 10],
  lydian:     [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  harmMinor:  [0, 2, 3, 5, 7, 8, 11],
  wholetone:  [0, 2, 4, 6, 8, 10],
};

export const BASE_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export const SHIP = {
  SPEED: 50,
  BOOST_SPEED: 150,
  ROTATION_SPEED: 2,
  LOOK_SPEED: 0.008,
  LANDING_HEIGHT: 3,
  ORBIT_HEIGHT: 200,
  WARP_DURATION: 3,
};

export const PLANET = {
  SURFACE_RADIUS: 75,
  // Scatter densities are defined for a reference radius; generators scale
  // counts by (planetRadius / DENSITY_REF_RADIUS)^2 so big planets stay as
  // lush as small ones instead of diluting a fixed count over more area.
  DENSITY_REF_RADIUS: 75,
  ATMOSPHERE_SCALE: 1.15,
  MAX_CREATURES: 150,
  CLAIM_RANGE: 20,
};

export const PLAYER = {
  // Camera
  HEIGHT: 1.8,
  LOOK_SPEED_TOUCH: 0.008,
  LOOK_SPEED_MOUSE: 0.004,

  // Ground movement — acceleration-based for Outer Wilds feel
  WALK_SPEED: 7,           // target ground speed
  RUN_SPEED: 13,           // sprint target speed
  GROUND_ACCEL: 60,        // how fast you reach target speed
  GROUND_FRICTION: 10,     // tangential drag when no input (higher = stop faster)
  AIR_ACCEL: 12,           // reduced control in air
  AIR_FRICTION: 0.3,       // very low drag in air

  // Jump & gravity
  JUMP_IMPULSE: 12,        // initial upward impulse
  GRAVITY: 22,             // m/s² toward planet center (always positive)
  JUMP_LOCKOUT: 0.25,      // seconds after jump where ground snap is disabled

  // Jetpack
  JETPACK_THRUST: 18,      // upward force while holding Space in air
  JETPACK_FUEL_MAX: 2.5,   // seconds of fuel
  JETPACK_RECHARGE: 1.8,   // fuel recharged per second on ground
  JETPACK_DRAIN: 1.0,      // fuel drained per second while thrusting

  // Boost (Shift while airborne — lateral burst)
  BOOST_IMPULSE: 20,       // one-shot lateral burst
  BOOST_COOLDOWN: 1.5,     // seconds between boosts

  // Terrain
  TERRAIN_SAMPLES: 8,      // multi-sample for smooth ground (higher = smoother)
  TERRAIN_SAMPLE_RADIUS: 1.0, // spread in meters (wider = smoother slopes)
  GROUND_SNAP: 0.25,       // snap-to-ground tolerance (must be < jump first-frame movement)
  TERRAIN_SMOOTH_SPEED: 28, // exponential follow speed (higher = more responsive)
};

export const ROCKS = {
  CLUSTERS_MIN: 6,           // fewest rock outcrops on any planet
  CLUSTERS_MAX: 9,           // most rock outcrops
  PER_CLUSTER_MIN: 3,        // rocks per outcrop
  PER_CLUSTER_MAX: 6,
  CLUSTER_FOOTPRINT: 13,     // reserved zone radius per outcrop (world units)
  MIN_RADIUS: 2.5,           // smallest rock (world units)
  MAX_RADIUS: 7.0,           // largest rock
  SUBDIVISION: 5,            // icosahedron detail → 10 242 verts per rock
  EMBED_FRACTION: 0.35,      // fraction of radius buried in terrain
  MIN_SPACING: 8,            // min surface distance between rocks in an outcrop
  BASE_HEALTH: 50,           // hits for smallest rock (scales with size)
  HIGHLIGHT_INTENSITY: 0.12, // emissive pulse when targeted
};

/* ── Flora (trees & foliage) ─────────────────────────────────── */

export const FLORA = {
  // Trees
  MIN_TREES: 150,
  MAX_TREES: 300,
  TREE_MIN_SPACING: 4,        // min surface distance between trees
  TREE_MIN_SCALE: 1.5,
  TREE_MAX_SCALE: 4.0,

  // Small foliage (instanced)
  MIN_FOLIAGE: 2500,
  MAX_FOLIAGE: 5000,
  FOLIAGE_MIN_SCALE: 1.5,
  FOLIAGE_MAX_SCALE: 3.5,
};

/* ── Crystal deposits ────────────────────────────────────────── */

export const CRYSTALS = {
  LANDMARK_CHANCE: 0.65,     // chance each ruin gets a crystal field at its rim
  VEINS_MIN: 2,              // standalone crystal fields beyond the ruin ones
  VEINS_MAX: 3,
  PER_FIELD_MIN: 2,          // crystal clusters per field
  PER_FIELD_MAX: 4,
  FIELD_FOOTPRINT: 9,        // reserved zone radius per field (world units)
  MIN_RADIUS: 2.5,
  MAX_RADIUS: 5.5,
  MIN_SPACING: 7,            // min surface distance between clusters in a field
  BASE_HEALTH: 80,
  RESOURCES: {
    rocky:   ['Rare Earth', 'Platinum Vein', 'Star Metal'],
    ice:     ['Cryogenic Core', 'Aurora Shard', 'Glacial Gem'],
    lava:    ['Inferno Ruby', 'Magma Pearl', 'Volcanic Heart'],
    ocean:   ['Abyssal Sapphire', 'Tide Crystal', 'Deep Pearl'],
    crystal: ['Prismatic Core', 'Rainbow Shard', 'Ethereal Diamond'],
    forest:  ['Life Crystal', 'Spirit Amber', 'Ancient Resin'],
    desert:  ['Sun Stone', 'Mirage Opal', 'Golden Topaz'],
    gas:     ['Storm Diamond', 'Lightning Opal', 'Nebula Shard'],
  },
};

/* ── Surface layout / zoning (see planet/SurfacePlan.js) ─────── */

export const LAYOUT = {
  SPAWN_CLEARING: 16,   // open ground around the ship landing site (world units)
  SITE_CLEARING: 14,    // barren clearing kept around each anomaly landmark
  STORY_CLEARING: 20,   // clearing around the authored story sites (home planet)
  BEACON_HEIGHT: 46,    // light column above unexplored anomalies
  BEACON_RADIUS: 0.7,
};

/* ── Anomalies / Ruins (Points of Interest) ──────────────────── */

export const ANOMALIES = {
  MIN_PER_PLANET: 4,
  MAX_PER_PLANET: 6,
  LOOT: {
    alien_ruin:        ['Ancient Tablet', 'Ruin Map Fragment', 'Alien Alloy'],
    crashed_ship:      ['Ship Log', 'Salvaged Engine Part', 'Emergency Beacon'],
    monolith:          ['Star Chart', 'Void Rune', 'Resonance Crystal'],
    abandoned_outpost: ['Data Core', 'Supply Cache', 'Repair Kit'],
    artifact:          ['Alien Artifact', 'Dimensional Key', 'Unknown Device'],
  },
};

/* ── Repair Tools (narrative progression) ────────────────────── */

/**
 * The player must find all REPAIR_TOOLS from ruins to fix the ship radio.
 * Each tool maps to a ruin type. The planet always spawns enough ruins.
 */
export const REPAIR_TOOLS = [
  { id: 'antenna_coil',    name: 'Antenna Coil',    ruinType: 'alien_ruin',        icon: '📡', description: 'A coiled antenna fragment, still humming faintly.' },
  { id: 'signal_crystal',  name: 'Signal Crystal',  ruinType: 'monolith',          icon: '💎', description: 'A translucent crystal that resonates at radio frequencies.' },
  { id: 'circuit_board',   name: 'Circuit Board',   ruinType: 'abandoned_outpost', icon: '🔧', description: 'A salvaged circuit board, worn but functional.' },
  { id: 'power_cell',      name: 'Power Cell',      ruinType: 'crashed_ship',      icon: '🔋', description: 'A compact energy cell with residual charge.' },
];

/* ── Geysers / Thermal Vents ─────────────────────────────────── */

export const GEYSERS = {
  FIELDS_MIN: 2,             // thermal basins per planet
  FIELDS_MAX: 3,
  PER_FIELD_MIN: 3,          // geysers per basin
  PER_FIELD_MAX: 6,
  FIELD_FOOTPRINT: 14,       // reserved zone radius per basin (world units)
  MIN_SPACING: 8,            // min surface distance between geysers in a basin
  MIN_PERIOD: 6,    // seconds between eruptions (fastest geyser)
  MAX_PERIOD: 20,   // seconds between eruptions (slowest geyser)
  MIN_DURATION: 1.5, // shortest eruption
  MAX_DURATION: 4,   // longest eruption
};

/* ── Harmony / Musical System ────────────────────────────────── */

export const HARMONY = {
  // Creature voice patterns — soft, musical timbres only
  VOICE_WAVEFORMS: ['sine', 'triangle'],
  VOICE_PATTERNS: ['chirp', 'hum', 'trill', 'warble', 'drone', 'bell', 'chime'],

  // Call-and-response: intervals (in scale degrees) for harmonic responses
  RESPONSE_INTERVALS: [2, 4, 3, 5, 1],

  // Timing — default 4/4 but overridden per-system
  DEFAULT_TIME_SIGNATURE: [4, 4],  // [beats, subdivision]
  DEFAULT_BEATS_PER_BAR: 4,
  MIN_CALL_INTERVAL: 4,      // minimum seconds between flock pattern repeats
  MAX_CALL_INTERVAL: 8,      // maximum seconds between flock pattern repeats
  RESPONSE_DELAY_BEATS: 0.5, // beats of delay before a flock-mate joins
  CALL_DURATION_MIN: 0.1,
  CALL_DURATION_MAX: 0.5,

  // Flocking — tighter groups
  FLOCK_RADIUS: 25,           // how close creatures must be to flock
  FLOCK_SEPARATION: 2.0,      // minimum distance between flock members
  FLOCK_ALIGNMENT: 0.8,       // alignment weight
  FLOCK_COHESION: 0.7,        // cohesion weight (stronger → tighter groups)
  FLOCK_SEPARATION_WEIGHT: 1.5,
  FLOCK_MAX_SPEED: 3,
  FLOCK_WANDER: 0.35,         // random wander force (lower → more orderly)

  // Species: how many distinct species per planet
  MIN_SPECIES: 3,
  MAX_SPECIES: 8,
  MIN_PER_SPECIES: 5,
  MAX_PER_SPECIES: 12,

  // Spawn clustering — how tightly each species group starts
  CLUSTER_SPREAD: 0.25,      // radians of spread around the cluster anchor

  // Preferred musical scales for pleasant sounds
  PLEASANT_SCALES: ['pentatonic', 'major', 'lydian', 'mixolydian', 'dorian'],

  // Genre-specific scale preferences
  GENRE_SCALES: {
    orchestral:   ['major', 'minor', 'lydian', 'dorian', 'pentatonic'],
    ambient:      ['pentatonic', 'wholetone', 'lydian', 'major'],
    jazz:         ['dorian', 'mixolydian', 'lydian', 'minor', 'blues', 'harmMinor'],
    breakcore:    ['phrygian', 'harmMinor', 'blues', 'minor', 'pentatonic'],
    electronic:   ['minor', 'phrygian', 'pentatonic', 'dorian'],
    minimal:      ['pentatonic', 'major', 'dorian'],
    folkAcoustic: ['major', 'mixolydian', 'dorian', 'pentatonic'],
    cinematic:    ['minor', 'harmMinor', 'lydian', 'phrygian', 'dorian'],
    lofi:         ['pentatonic', 'dorian', 'mixolydian', 'major'],
    world:        ['phrygian', 'harmMinor', 'dorian', 'blues', 'pentatonic'],
  },

  /**
   * Instrument roles — each species gets one.
   * Defines the Tone.js synth type, musical behavior, and pattern style.
   *
   * rhythmDensity: how many notes per 4-beat bar (sparse → busy)
   * octaveRange: [min, max] offsets from planet root octave
   */
  INSTRUMENT_ROLES: [
    { id: 'bass',       label: 'Bass',       sampleFolder: 'bass-electric', sampleMapping: { "E1": "E1.mp3", "G1": "G1.mp3", "A#1": "As1.mp3", "C#2": "Cs2.mp3", "E2": "E2.mp3", "G2": "G2.mp3", "A#2": "As2.mp3", "C#3": "Cs3.mp3", "E3": "E3.mp3", "G3": "G3.mp3" }, rhythmDensity: [1, 3],  octaveRange: [-1, 0], noteDuration: [0.4, 1.0],  useNoise: false },
    { id: 'percussion', label: '🥁 Drums',  synthType: 'membrane',                                                                                     rhythmDensity: [4, 8],  octaveRange: [-1, 0], noteDuration: [0.05, 0.12], useNoise: true  },
    { id: 'pad',        label: 'Pad',        sampleFolder: 'organ',         sampleMapping: { "C2": "C2.mp3", "A2": "A2.mp3", "C3": "C3.mp3", "A3": "A3.mp3", "C4": "C4.mp3", "A4": "A4.mp3", "C5": "C5.mp3" }, rhythmDensity: [1, 3],  octaveRange: [0, 1],  noteDuration: [1.0, 2.5],  useNoise: false },
    { id: 'keys',       label: 'Keys',       sampleFolder: 'piano',         sampleMapping: { "A1": "A1.mp3", "C2": "C2.mp3", "A2": "A2.mp3", "C3": "C3.mp3", "A3": "A3.mp3", "C4": "C4.mp3", "A4": "A4.mp3", "C5": "C5.mp3", "A5": "A5.mp3", "C6": "C6.mp3" }, rhythmDensity: [3, 6],  octaveRange: [0, 1],  noteDuration: [0.15, 0.4], useNoise: false },
    { id: 'pluck',      label: 'Guitar',     sampleFolder: 'guitar-nylon',  sampleMapping: { "E2": "E2.mp3", "F#2": "Fs2.mp3", "A2": "A2.mp3", "C#3": "Cs3.mp3", "D3": "D3.mp3", "G3": "G3.mp3", "B3": "B3.mp3", "C#4": "Cs4.mp3", "E4": "E4.mp3", "F#4": "Fs4.mp3", "A4": "A4.mp3", "D5": "D5.mp3" }, rhythmDensity: [3, 6],  octaveRange: [0, 1],  noteDuration: [0.1, 0.35], useNoise: false },
    { id: 'lead',       label: 'Lead',       sampleFolder: 'flute',         sampleMapping: { "C4": "C4.mp3", "E4": "E4.mp3", "A4": "A4.mp3", "C5": "C5.mp3", "E5": "E5.mp3", "A5": "A5.mp3", "C6": "C6.mp3" }, rhythmDensity: [4, 8],  octaveRange: [1, 2],  noteDuration: [0.1, 0.4],  useNoise: false },
    { id: 'arp',        label: 'Bells',      sampleFolder: 'xylophone',     sampleMapping: { "G4": "G4.mp3", "C5": "C5.mp3", "G5": "G5.mp3", "C6": "C6.mp3", "G6": "G6.mp3", "C7": "C7.mp3" }, rhythmDensity: [4, 8],  octaveRange: [0, 1],  noteDuration: [0.1, 0.3],  useNoise: false },
    { id: 'atmosphere', label: 'Strings',    sampleFolder: 'violin',        sampleMapping: { "A3": "A3.mp3", "C4": "C4.mp3", "E4": "E4.mp3", "G4": "G4.mp3", "A4": "A4.mp3", "C5": "C5.mp3", "E5": "E5.mp3", "G5": "G5.mp3", "A5": "A5.mp3", "C6": "C6.mp3" }, rhythmDensity: [1, 3],  octaveRange: [1, 2],  noteDuration: [0.8, 2.0],  useNoise: false },
  ],
};